import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, CircleMarker, Tooltip } from 'react-leaflet';
import { Users as UsersIcon, MapPin } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardValue, CardSubtitle } from '@/components/ui/Card';
import { SortableColumn, useTableSort } from '@/components/ui/SortableColumn';
import { api } from '@/lib/api';
import { formatNumber } from '@/lib/utils';
import 'leaflet/dist/leaflet.css';

// Sprint 2.6 do AdminReformulacao_v1.md — visão agregada da base por
// região. Alimentada por /admin/users/locations (agregado de
// profiles.city + state pra users ativos).
//
// Mapa: Leaflet + OpenStreetMap (gratuito, sem API key). Marcadores só
// pra cidades com >= 3 usuários (evita poluição visual quando muitas
// cidades têm 1 tester). Coordenadas via lookup estático — as 27
// capitais + principais cidades cobertas. Cidades não mapeadas ainda
// aparecem na tabela ao lado.

// Coordenadas de referência: capitais + cidades grandes com Amuzze presente.
// Adicionar aqui novas cidades conforme aparecem — o mapa é um bônus
// visual, tabela abaixo é a fonte completa.
const CITY_COORDS: Record<string, [number, number]> = {
  // Capitais
  'São Paulo':      [-23.5505, -46.6333],
  'Rio de Janeiro': [-22.9068, -43.1729],
  'Brasília':       [-15.7801, -47.9292],
  'Salvador':       [-12.9718, -38.5011],
  'Fortaleza':      [-3.7327, -38.5270],
  'Belo Horizonte': [-19.9167, -43.9345],
  'Manaus':         [-3.1190, -60.0217],
  'Curitiba':       [-25.4284, -49.2733],
  'Recife':         [-8.0476, -34.8770],
  'Porto Alegre':   [-30.0346, -51.2177],
  'Belém':          [-1.4558, -48.4902],
  'Goiânia':        [-16.6799, -49.2550],
  'Guarulhos':      [-23.4538, -46.5333],
  'Campinas':       [-22.9099, -47.0626],
  'São Luís':       [-2.5307, -44.3068],
  'São Gonçalo':    [-22.8272, -43.0537],
  'Maceió':         [-9.6498, -35.7089],
  'Duque de Caxias':[-22.7856, -43.3117],
  'Natal':          [-5.7945, -35.2110],
  'Teresina':       [-5.0892, -42.8016],
  'Campo Grande':   [-20.4697, -54.6201],
  'São Bernardo do Campo': [-23.6944, -46.5654],
  'Nova Iguaçu':    [-22.7592, -43.4511],
  'João Pessoa':    [-7.1195, -34.8451],
  'Santo André':    [-23.6633, -46.5309],
  'Osasco':         [-23.5325, -46.7920],
  'São José dos Campos': [-23.2237, -45.9009],
  'Jaboatão dos Guararapes': [-8.1130, -35.0148],
  'Ribeirão Preto': [-21.1775, -47.8103],
  'Uberlândia':     [-18.9186, -48.2772],
  'Contagem':       [-19.9317, -44.0536],
  'Sorocaba':       [-23.5015, -47.4526],
  'Aracaju':        [-10.9091, -37.0677],
  'Feira de Santana': [-12.2664, -38.9663],
  'Cuiabá':         [-15.6014, -56.0979],
  'Joinville':      [-26.3045, -48.8487],
  'Juiz de Fora':   [-21.7642, -43.3496],
  'Londrina':       [-23.3103, -51.1628],
  'Aparecida de Goiânia': [-16.8237, -49.2436],
  'Ananindeua':     [-1.3656, -48.3722],
  'Porto Velho':    [-8.7619, -63.9039],
  'Serra':          [-20.1218, -40.3078],
  'Niterói':        [-22.8832, -43.1034],
  'Belford Roxo':   [-22.7642, -43.3993],
  'Caxias do Sul':  [-29.1678, -51.1794],
  'Campos dos Goytacazes': [-21.7622, -41.3181],
  'Vila Velha':     [-20.3222, -40.2925],
  'Florianópolis':  [-27.5949, -48.5482],
  'São José do Rio Preto': [-20.8113, -49.3758],
  'Macapá':         [0.0349, -51.0694],
  'Mauá':           [-23.6678, -46.4611],
  'Carapicuíba':    [-23.5225, -46.8358],
  'Olinda':         [-8.0089, -34.8553],
  'Boa Vista':      [2.8235, -60.6758],
  'Diadema':        [-23.6862, -46.6228],
  'Piracicaba':     [-22.7253, -47.6493],
  'Vitória':        [-20.3155, -40.3128],
  'Palmas':         [-10.2491, -48.3243],
  'Tangará da Serra': [-14.6229, -57.4931],
};

// Bounding box aproximado do Brasil pra fitBounds inicial.
const BRAZIL_BOUNDS: [[number, number], [number, number]] = [[-33.8, -73.9], [5.3, -34.7]];

function markerRadius(count: number): number {
  // Escala logarítmica pra não deixar cidades grandes engolirem o mapa.
  if (count <= 1) return 5;
  if (count <= 5) return 7;
  if (count <= 20) return 10;
  if (count <= 100) return 14;
  return 18;
}

// Listas vazias ESTÁVEIS — mesmo motivo do `SEM_LINHAS` de `Financas.tsx`, e
// aqui o linter cobrou primeiro: um `[]` novo a cada render dá identidade nova
// à dependência sem mudar o conteúdo, e o `useMemo` dos marcadores recalcula
// SEMPRE enquanto a consulta não volta — que é justamente o tempo em que a
// tela está no ar esperando dado.
//
// 📌 Uma constante por tipo, em vez de uma só genérica: assim a lista vazia
// continua tipada e o `tsc -b` segue conferindo o que entra em cada tabela.
const SEM_CIDADES: { city: string; state: string; count: number }[] = [];
const SEM_ESTADOS: { state: string; count: number }[] = [];

export function Localizacao() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users-locations'],
    queryFn: async () => {
      const { data } = await api.get('/admin/users/locations', { params: { min_per_city: 1 } });
      return data?.data as { by_state: { state: string; count: number }[]; by_city: { city: string; state: string; count: number }[]; total_with_location: number };
    },
  });

  const byCity  = data?.by_city  ?? SEM_CIDADES;
  const byState = data?.by_state ?? SEM_ESTADOS;
  const total   = data?.total_with_location ?? 0;

  const { sorted: sortedCities, sort, handleSort } = useTableSort(byCity, 'count', 'desc');
  const { sorted: sortedStates, sort: sortState, handleSort: handleSortState } = useTableSort(byState, 'count', 'desc');

  // Marcadores só pra cidades com coords conhecidas + count >= 3.
  const markers = useMemo(() => {
    return byCity
      .filter((c) => c.count >= 3 && CITY_COORDS[c.city])
      .map((c) => ({
        city: c.city,
        state: c.state,
        count: c.count,
        coords: CITY_COORDS[c.city],
      }));
  }, [byCity]);

  return (
    <Layout title="Localização">
      <p className="mb-6 text-sm text-text-muted">
        Distribuição dos usuários por região. Mapa mostra cidades com pelo menos 3 usuários; tabela lista todas.
        Coleta baseada em <span className="text-brand">profiles.city</span> (Sprint 2.5).
      </p>

      <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>Total com localização</CardTitle>
            <UsersIcon size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(total)}</CardValue>
          <CardSubtitle>usuários com cidade preenchida</CardSubtitle>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Cidades</CardTitle>
            <MapPin size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(byCity.length)}</CardValue>
          <CardSubtitle>distintas na base</CardSubtitle>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Estados</CardTitle>
            <MapPin size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(byState.length)}</CardValue>
          <CardSubtitle>com pelo menos 1 usuário</CardSubtitle>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>No mapa</CardTitle>
            <MapPin size={18} className="text-brand" />
          </CardHeader>
          <CardValue>{formatNumber(markers.length)}</CardValue>
          <CardSubtitle>cidades com 3+ usuários</CardSubtitle>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Card className="overflow-hidden p-0">
            <div className="h-[520px] w-full">
              {isLoading ? (
                <div className="flex h-full items-center justify-center text-text-muted">Carregando mapa…</div>
              ) : (
                <MapContainer
                  bounds={BRAZIL_BOUNDS}
                  scrollWheelZoom={true}
                  style={{ height: '100%', width: '100%' }}
                  className="!bg-bg-primary"
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {markers.map((m) => (
                    <CircleMarker
                      key={`${m.city}-${m.state}`}
                      center={m.coords}
                      radius={markerRadius(m.count)}
                      pathOptions={{ color: '#a855f7', fillColor: '#a855f7', fillOpacity: 0.6, weight: 1.5 }}
                    >
                      <Tooltip direction="top">
                        <div>
                          <strong>{m.city}</strong> / {m.state}<br />
                          {m.count} usuário{m.count > 1 ? 's' : ''}
                        </div>
                      </Tooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              )}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="p-0 overflow-hidden">
            <div className="border-b border-border bg-bg-input px-4 py-2 text-xs uppercase text-text-secondary">
              Estados
            </div>
            <div className="max-h-60 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase text-text-secondary">
                  <tr>
                    <SortableColumn label="UF" field="state" sort={sortState} onSort={handleSortState} />
                    <SortableColumn label="Usuários" field="count" sort={sortState} onSort={handleSortState} align="right" />
                  </tr>
                </thead>
                <tbody>
                  {sortedStates.map((s) => (
                    <tr key={s.state} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 text-text-secondary">{s.state}</td>
                      <td className="px-4 py-2 text-right font-medium text-brand">{formatNumber(s.count)}</td>
                    </tr>
                  ))}
                  {sortedStates.length === 0 && !isLoading && (
                    <tr><td colSpan={2} className="p-4 text-center text-xs text-text-muted">Sem dados</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>

      <h2 className="mt-6 mb-3 text-sm font-medium text-text-secondary">Todas as cidades</h2>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-bg-input text-left text-xs uppercase text-text-secondary">
            <tr>
              <SortableColumn label="Cidade" field="city" sort={sort} onSort={handleSort} />
              <SortableColumn label="UF" field="state" sort={sort} onSort={handleSort} />
              <SortableColumn label="Usuários" field="count" sort={sort} onSort={handleSort} align="right" />
              <th className="px-4 py-3">No mapa?</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={4} className="p-8 text-center text-text-muted">Carregando…</td></tr>
            )}
            {sortedCities.map((c) => (
              <tr key={`${c.city}-${c.state}`} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium text-text-primary">{c.city}</td>
                <td className="px-4 py-3 text-text-secondary">{c.state || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold text-brand">{formatNumber(c.count)}</td>
                <td className="px-4 py-3 text-xs text-text-muted">
                  {CITY_COORDS[c.city] && c.count >= 3 ? '✓' : '—'}
                </td>
              </tr>
            ))}
            {!isLoading && sortedCities.length === 0 && (
              <tr><td colSpan={4} className="p-8 text-center text-text-muted">Sem cidades cadastradas ainda.</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </Layout>
  );
}
